import type { JSX, ReactNode } from "react";
import ReactMarkdown, { type Components, defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

import type { ReportRevisionSource } from "@/lib/reports";

interface ReportMarkdownProps {
  markdown: string;
  sources: ReportRevisionSource[];
}

const SECTION_LINK = /^#report-section-[A-Za-z0-9_-]+$/;

function safeUrlTransform(url: string): string {
  if (SECTION_LINK.test(url) || url.startsWith("source:")) return url;
  return defaultUrlTransform(url);
}

function safeLink(
  href: string | undefined,
  children: ReactNode,
  sources: ReportRevisionSource[],
): JSX.Element {
  if (href && SECTION_LINK.test(href)) {
    return (
      <a className="font-medium text-[var(--report-info)] underline underline-offset-2" href={href}>
        {children}
      </a>
    );
  }
  if (href?.startsWith("source:")) {
    const handle = href.slice("source:".length);
    const source = sources.find((candidate) => candidate.handle === handle);
    if (source?.public_url) {
      return (
        <a
          className="font-medium text-[var(--report-info)] underline underline-offset-2"
          href={source.public_url}
          rel="noreferrer"
          target="_blank"
        >
          {children}
        </a>
      );
    }
    if (source) return <span data-source-handle={handle}>{children}</span>;
  }
  return <span>{children}</span>;
}

export function ReportMarkdown({ markdown, sources }: ReportMarkdownProps): JSX.Element {
  const components: Components = {
    a: ({ href, children }) => safeLink(href, children, sources),
    h1: ({ children }) => <h3 className="mt-5 text-lg font-semibold">{children}</h3>,
    h2: ({ children }) => <h3 className="mt-5 text-lg font-semibold">{children}</h3>,
    h3: ({ children }) => <h4 className="mt-4 font-semibold">{children}</h4>,
    p: ({ children }) => <p className="my-3 leading-7">{children}</p>,
    ul: ({ children }) => <ul className="my-3 list-disc space-y-1 pl-5">{children}</ul>,
    ol: ({ children }) => <ol className="my-3 list-decimal space-y-1 pl-5">{children}</ol>,
    blockquote: ({ children }) => (
      <blockquote className="my-4 border-l-2 border-[var(--report-accent)] pl-4 text-[var(--report-muted-fg)]">
        {children}
      </blockquote>
    ),
    code: ({ children }) => (
      <code className="rounded bg-[var(--report-muted)] px-1 py-0.5 font-mono text-[0.9em]">
        {children}
      </code>
    ),
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto">
        <table className="w-full border-collapse text-left text-sm">{children}</table>
      </div>
    ),
    th: ({ children }) => (
      <th className="border-b border-[var(--report-border)] bg-[var(--report-muted)] px-3 py-2 font-semibold">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border-b border-[var(--report-border)] px-3 py-2">{children}</td>
    ),
  };

  return (
    <div className="report-markdown max-w-none text-[var(--report-fg)]">
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfm]}
        skipHtml
        urlTransform={safeUrlTransform}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
